import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Sparkles, Crown } from "lucide-react";
import { toast } from "sonner";
import { Tunduk } from "@/components/Avatar";
import { LangToggle } from "@/components/Header";
import { UserChip } from "@/components/UserChip";
import { useLang } from "@/context/LangContext";
import { fetchStats, generateCards } from "@/lib/api";
import { getBest } from "@/lib/engine";

export default function Landing() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [gen, setGen] = useState(false);

  useEffect(() => { fetchStats().then(setStats).catch(() => {}); }, [gen]);

  const onGenerate = async () => {
    setGen(true);
    try {
      const cards = await generateCards(3);
      toast.success(`${t.generated} ${cards.length}`);
    } catch {
      toast.error(t.genFailed);
    } finally {
      setGen(false);
    }
  };

  return (
    <div className="max-w-md mx-auto min-h-dvh px-5 py-4 flex flex-col" data-testid="landing-screen">
      <div className="flex justify-between items-center gap-3"><UserChip compact /><LangToggle /></div>
      <div className="flex-1 flex flex-col justify-center items-start gap-6 py-8">
        <div className="rise relative">
          <div className="absolute -inset-6 rounded-full bg-[var(--gold)]/20 blur-2xl" />
          <Tunduk size={120} className="spin-slow relative" />
        </div>
        <div className="rise d1">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[var(--gold)]">{t.country} 🇰🇬</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mt-2" data-testid="landing-title">{t.appTitle}</h1>
        </div>
        <p className="rise d2 text-base text-[var(--text-light)]/80 max-w-xs leading-relaxed" data-testid="landing-tagline">{t.tagline}</p>
        <div className="rise d3 flex flex-col gap-3 w-full">
          <button onClick={() => navigate("/intro")} className="btn-gold w-full py-4 text-lg flex items-center justify-center gap-2" data-testid="become-president-btn">
            <Crown size={22} /> {t.become}
          </button>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => navigate("/leaderboard")} className="btn-ghost py-3 text-sm flex items-center justify-center gap-2" data-testid="leaderboard-btn"><Trophy size={16} /> {t.leaderboard}</button>
            <button onClick={onGenerate} disabled={gen} className="btn-ghost py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="generate-ai-cards-btn"><Sparkles size={16} className={gen ? "animate-spin" : ""} /> {gen ? t.generating : t.freshCards}</button>
          </div>
        </div>
        <div className="rise d4 panel w-full px-4 py-3 flex items-center justify-between text-sm">
          <div><div className="text-[10px] uppercase tracking-widest text-[var(--text-light)]/60">{t.personalBest}</div><div className="font-display font-extrabold text-xl text-[var(--gold)]" data-testid="personal-best-value">{getBest()}</div></div>
          {stats && <div className="text-right"><div className="font-display font-extrabold text-xl" data-testid="stats-games-played">{stats.games_played}</div><div className="text-[10px] uppercase tracking-widest text-[var(--text-light)]/60">{t.presidentsServed}</div></div>}
        </div>
      </div>
    </div>
  );
}
